import { useFormikContext } from "formik";
import { Box, Button, TextField, Typography } from "@mui/material";
import DevelopmentStageSelect from "./DevelopmentStageSelect";
import SDGSelect from "./SDGSelect";
import DateSelect from "../../../common/DateSelect";
import CoordinatesInput from "./CoordinatesInput";
import LocationPinMapLinkField from "./LocationPinMapLinkField";
import ReadyForSaleCheckbox from "./ReadyForSaleCheckbox";
import NotReadyForSaleReasonSelect from "./NotReadyForSaleReasonSelect";
import CarbonOffsetCheckbox from "./CarbonOffsetCheckbox";
import InterventionsContainer from "./interventions";
import OutcomesContainer from "./outcomes";
import InternalDetailsContainer from "./internal-details";
import RiskMonitoringContainer from "./risk-monitoring";
import CountryMultiSelect from "./CountryMultiSelect";
import { AddSolutionFormValues } from "../../add/form/types";
import { EditSolutionFormValues } from "../../edit/form/types";
import { WriteableSolutionFormContainerProps } from "../types";
import ScoresContainer from "./scores";
import CommercialInfoContainer from "./commercial-info";
import NarrativeContainer from "./narrative";
import BenefitsContainer from "./benefits";
import SatelliteImageryContainer from "./satellite-imagery";
import ShortDescriptionField from "./ShortDescriptionField";

export default function WriteableSolutionFormContainer({
  handleCancel,
  submitLoading,
  submitDisabled,
  title,
  assessmentMethodologyField,
  interventionTypeField,
  programmeInfoSection,
  actionsMarginBottom = 0,
}: WriteableSolutionFormContainerProps) {
  const { handleSubmit, handleChange, values, touched, errors, resetForm } =
    useFormikContext<AddSolutionFormValues | EditSolutionFormValues>();

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit}
        data-testid="solution-form"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" sx={{ paddingBottom: 2, paddingTop: 2 }}>
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "50%",
          }}
        >
          <TextField
            id="solution-name"
            label="Name"
            variant="standard"
            sx={{ paddingBottom: 2 }}
            autoComplete="disabled"
            onChange={handleChange}
            value={values["solution-name"]}
            error={touched["solution-name"] && Boolean(errors["solution-name"])}
            helperText={touched["solution-name"] && errors["solution-name"]}
          />
          <TextField
            id="solution-vbc-website-display-name"
            label="VBC Website Display Name"
            variant="standard"
            sx={{ paddingBottom: 2 }}
            autoComplete="disabled"
            onChange={handleChange}
            value={values["solution-vbc-website-display-name"]}
            error={
              touched["solution-vbc-website-display-name"] &&
              Boolean(errors["solution-vbc-website-display-name"])
            }
            helperText={
              touched["solution-vbc-website-display-name"] &&
              errors["solution-vbc-website-display-name"]
            }
          />
          <Box sx={{ paddingBottom: 2 }}>
            <CountryMultiSelect />
          </Box>
          <ShortDescriptionField />
          <TextField
            id="solution-developer"
            label="Developer"
            variant="standard"
            sx={{ paddingBottom: 2 }}
            autoComplete="disabled"
            onChange={handleChange}
            value={values["solution-developer"]}
          />
          <Box sx={{ paddingBottom: 2 }}>
            <DevelopmentStageSelect />
          </Box>
          <Box sx={{ paddingBottom: 2 }}>{assessmentMethodologyField}</Box>
          <Box sx={{ paddingBottom: 2 }}>
            <SDGSelect />
          </Box>
          <Box sx={{ paddingBottom: 2 }}>
            <DateSelect<"solution">
              dateName="solution"
              minDate={new Date("01/01/2018")}
              maxDate={new Date("01/01/2222")}
            />
          </Box>
          <Box sx={{ paddingBottom: 2 }}>
            <CoordinatesInput />
          </Box>
          <Box sx={{ paddingBottom: 2 }}>{interventionTypeField}</Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 2,
              paddingBottom: 2,
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <ReadyForSaleCheckbox />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <NotReadyForSaleReasonSelect />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              direction: "row",
              alignItems: "start",
              paddingBottom: 4,
            }}
          >
            <CarbonOffsetCheckbox />
          </Box>
          <Box
            id="intervention-container"
            sx={{
              paddingBottom: 4,
            }}
          >
            <InterventionsContainer open />
          </Box>
          <Box
            id="benefits-container"
            sx={{
              paddingBottom: 4,
            }}
          >
            <BenefitsContainer open />
          </Box>
          <Box
            id="scores-container"
            sx={{
              paddingBottom: 4,
            }}
          >
            <ScoresContainer open />
          </Box>
          <Box
            id="outcomes-container"
            sx={{
              paddingBottom: 4,
            }}
          >
            <OutcomesContainer open />
          </Box>
          <Box
            id="programme-info-container"
            sx={{
              paddingBottom: 4,
            }}
          >
            {programmeInfoSection}
          </Box>
          <Box
            id="internal-details-container"
            sx={{
              paddingBottom: 4,
            }}
          >
            <InternalDetailsContainer open />
          </Box>
          <Box
            id="commercial-information-container"
            sx={{
              paddingBottom: 4,
            }}
          >
            <CommercialInfoContainer open />
          </Box>
          <Box
            id="satellite-imagery-container"
            sx={{
              paddingBottom: 4,
            }}
          >
            <SatelliteImageryContainer />
          </Box>
          <Box
            id="location-pin-map-link-container"
            sx={{
              paddingBottom: 4,
            }}
          >
            <LocationPinMapLinkField />
          </Box>
          <Box
            id="narrative-container"
            sx={{
              paddingBottom: 4,
            }}
          >
            <NarrativeContainer open />
          </Box>
          <Box
            id="risk-monitoring-container"
            sx={{
              paddingBottom: 4,
            }}
          >
            <RiskMonitoringContainer open />
          </Box>
        </Box>
        <Box
          sx={{
            backgroundColor: "transparent",
            display: "flex",
            flexDirection: "row",
            justifyContent: "end",
            width: "50%",
            position: "relative",
            bottom: 3,
            paddingRight: 2,
            marginBottom: actionsMarginBottom,
          }}
        >
          <Button
            onClick={() => {
              handleCancel();
              resetForm();
            }}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            data-testid="submit-solution-button"
            loading={submitLoading}
            disabled={submitDisabled}
            type="submit"
            variant="contained"
          >
            Submit
          </Button>
        </Box>
      </Box>
    </>
  );
}
